// src/components/Leave/PendingRequests.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCheckCircle, FiXCircle, FiRefreshCw, FiSearch, FiFilter, FiEye } from 'react-icons/fi';
import { useLeave } from '../../hooks/useLeave';
import StatusBadge from '../Common/StatusBadge';

const PendingRequests = () => {
  const navigate = useNavigate();
  const { getPendingRequests, approveLeave, rejectLeave, loading } = useLeave();

  const [requests, setRequests] = useState([]);
  const [pagination, setPagination] = useState({ count: 0, next: null, previous: null });
  const [filters, setFilters] = useState({ search: '', department: '' });
  const [processingId, setProcessingId] = useState(null);
  const [rejectModal, setRejectModal] = useState({ show: false, id: null, reason: '' });
  const [error, setError] = useState(null);

  const loadRequests = async (params = {}) => {
    setError(null);
    try {
      const data = await getPendingRequests({ ...filters, ...params });
      setRequests(data.results || data);
      setPagination({
        count: data.count || 0,
        next: data.next,
        previous: data.previous,
      });
    } catch (err) {
      setError(err.message || 'Failed to load pending requests');
    }
  };

  useEffect(() => {
    loadRequests();
  }, [filters]);

  const handleApprove = async (id) => {
    if (!window.confirm('Are you sure you want to approve this leave?')) return;

    setProcessingId(id);
    try {
      await approveLeave(id, { action: 'approve', remarks: 'Approved via dashboard' });
      await loadRequests();
    } catch (err) {
      setError(err.message || 'Failed to approve leave');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectClick = (id) => {
    setRejectModal({ show: true, id, reason: '' });
  };

  const handleRejectConfirm = async () => {
    const { id, reason } = rejectModal;
    if (!reason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    setProcessingId(id);
    try {
      await rejectLeave(id, { action: 'reject', reason: reason.trim() });
      setRejectModal({ show: false, id: null, reason: '' });
      await loadRequests();
    } catch (err) {
      setError(err.message || 'Failed to reject leave');
    } finally {
      setProcessingId(null);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  if (loading && requests.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Pending Leave Requests</h1>
          <p className="text-gray-500">Review and manage all pending leave requests</p>
        </div>
        <button
          onClick={() => loadRequests()}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition"
        >
          <FiRefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              name="search"
              placeholder="Search by employee name or ID..."
              value={filters.search}
              onChange={handleFilterChange}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div className="sm:w-48">
            <select
              name="department"
              value={filters.department}
              onChange={handleFilterChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Departments</option>
              <option value="Engineering">Engineering</option>
              <option value="HR">HR</option>
              <option value="Sales">Sales</option>
              <option value="Finance">Finance</option>
              <option value="Marketing">Marketing</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
          <button onClick={() => loadRequests()} className="ml-3 text-sm font-medium hover:underline">
            Retry
          </button>
        </div>
      )}

      {/* Requests List */}
      {requests.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <FiCheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <p className="text-gray-500">No pending requests</p>
          <p className="text-sm text-gray-400">All leave requests have been processed</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dates
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Days
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Department
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {requests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-800">{request.employee_name}</span>
                        <span className="text-sm text-gray-500">{request.employee_id}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {request.leave_type_display}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      <div>{request.start_date}</div>
                      <div className="text-xs text-gray-400">→ {request.end_date}</div>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-700">
                      {request.total_days}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {request.department}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleApprove(request.id)}
                          disabled={processingId === request.id}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition disabled:opacity-50"
                          title="Approve"
                        >
                          <FiCheckCircle className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleRejectClick(request.id)}
                          disabled={processingId === request.id}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
                          title="Reject"
                        >
                          <FiXCircle className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => navigate(`/leave/admin/requests/${request.id}`)}
                          className="p-2 text-gray-500 hover:bg-gray-50 rounded-lg transition"
                          title="View Details"
                        >
                          <FiEye className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.count > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-gray-500">
                Showing {requests.length} of {pagination.count} requests
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => loadRequests({ page: 1 })}
                  disabled={!pagination.previous}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                >
                  Previous
                </button>
                <button
                  onClick={() => loadRequests({ page: 2 })}
                  disabled={!pagination.next}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-2">Reject Leave Request</h3>
            <p className="text-gray-500 text-sm mb-4">Please provide a reason for rejection</p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rejection Reason *
              </label>
              <textarea
                rows="4"
                placeholder="Explain why this leave is being rejected..."
                value={rejectModal.reason}
                onChange={(e) => setRejectModal(prev => ({ ...prev, reason: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setRejectModal({ show: false, id: null, reason: '' })}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectConfirm}
                disabled={!rejectModal.reason.trim() || processingId === rejectModal.id}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
              >
                {processingId === rejectModal.id ? 'Processing...' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingRequests;